/**
 * NexoProvider - React context that exposes the nexo client, a fresh token
 * getter, and the resolved store info (language, id, name) to the whole tree.
 */

import type { NexoClient, StoreInfoResponse } from '@tiendanube/nexo';
import { getSessionToken } from '@tiendanube/nexo';
import { createContext, type ReactNode, useContext } from 'react';
import { createT, resolveLocale, type TFunction } from '@/lib/i18n';
import nexoClient from '@/lib/nexo-client';

export interface NexoContextValue {
  nexo: NexoClient;
  getToken: () => Promise<string>;
  storeInfo: StoreInfoResponse | null;
  locale: 'pt' | 'en';
  t: TFunction;
}

const NexoContext = createContext<NexoContextValue | null>(null);

interface NexoProviderProps {
  children: ReactNode;
  storeInfo: StoreInfoResponse | null;
}

export function NexoProvider({ children, storeInfo }: NexoProviderProps) {
  const locale = resolveLocale(storeInfo?.language);
  const t = createT(locale);

  const value: NexoContextValue = {
    nexo: nexoClient,
    getToken: () => getSessionToken(nexoClient),
    storeInfo,
    locale,
    t,
  };

  return <NexoContext.Provider value={value}>{children}</NexoContext.Provider>;
}

export function useNexo(): NexoContextValue {
  const ctx = useContext(NexoContext);
  if (!ctx) {
    throw new Error('useNexo must be used inside NexoProvider');
  }
  return ctx;
}
